import React, { useState } from 'react';
import { ConfigProvider, theme, Table, Avatar } from 'antd';
import { Helmet } from 'react-helmet-async';
import {
    ArrowRightToLine,
    ArrowLeftToLine,
    Settings,
    Plus,
    Filter,
    ListFilter,
    ArrowDown,
    ChevronDown,
    FolderOutput,
    FolderInput
} from 'lucide-react';
import Layout from '../../Layout'; // Giữ nguyên import Layout của bạn
import { Link, useNavigate } from 'react-router-dom';

export default function DecisionList() {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const navigate = useNavigate();

    // Cấu hình Theme Ant Design
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            borderRadius: 4,
        },
        components: {
            Table: {
                headerBg: 'transparent',
                headerColor: '#6b7280',
                headerBorderRadius: 0,
                rowHoverBg: isDarkMode ? '#374151' : '#f9fafb',
                paddingContentVertical: 12,
            }
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    // ================= MOCK DATA (Cập nhật trạng thái chữ ký số) =================
    const dataSource = [
        // Chờ duyệt
        {
            key: '1',
            creator: { text: 'H', color: '#0ea5e9' },
            decisionCode: '13/08/2026',
            decisionType: 'Quyết định điều chỉnh lương',
            signDate: '01/01/2026',
            effectiveDate: '01/01/2026',
            status: 'pending',
            createdAt: '25/12/2025'
        },
        {
            key: '2',
            creator: { img: 'https://i.pravatar.cc/150?u=1' },
            decisionCode: '13/08/2026',
            decisionType: 'Quyết định điều chỉnh lương',
            signDate: '15/02/2026',
            effectiveDate: '15/02/2026',
            status: 'pending',
            createdAt: '10/02/2026'
        },

        // Đã duyệt
        {
            key: '3',
            creator: { img: 'https://i.pravatar.cc/150?u=2' },
            decisionCode: '13/08/2026',
            decisionType: 'Quyết định khen thưởng',
            signDate: '10/09/2025',
            effectiveDate: '10/09/2025',
            status: 'approved',
            createdAt: '01/09/2025'
        },
        {
            key: '4',
            creator: { text: 'T', color: '#f97316' },
            decisionCode: '13/08/2026',
            decisionType: 'Quyết định kỷ luật',
            signDate: '01/04/2026',
            effectiveDate: '01/04/2026',
            status: 'approved',
            createdAt: '25/03/2026'
        },

        // Không duyệt
        {
            key: '5',
            creator: { img: 'https://i.pravatar.cc/150?u=4' },
            decisionCode: '13/08/2026',
            decisionType: 'Quyết định khen thưởng',
            signDate: '15/06/2024',
            effectiveDate: '15/06/2024',
            status: 'rejected',
            createdAt: '10/06/2024'
        }
    ];

    // Bộ đếm số lượng cho Tabs
    const counts = {
        all: dataSource.length,
        pending: dataSource.filter(d => d.status === 'pending').length,
        approved: dataSource.filter(d => d.status === 'approved').length,
        rejected: dataSource.filter(d => d.status === 'rejected').length,
    };

    const filteredData = activeTab === 'all' ? dataSource : dataSource.filter(item => item.status === activeTab);

    // ================= ĐỊNH NGHĨA CÁC CỘT =================
    const columns = [
        {
            // BỔ SUNG TIÊU ĐỀ: "Người tạo"
            title: 'Người tạo',
            dataIndex: 'creator',
            key: 'creator',
            width: 80,
            align: 'center',
            render: (creator) => (
                <div className="inline-block">
                    {creator.img ? (
                        <Avatar src={creator.img} size={28} className="cursor-pointer border border-gray-200" />
                    ) : (
                        <Avatar style={{ backgroundColor: creator.color }} size={28} className="cursor-pointer text-xs font-medium">
                            {creator.text}
                        </Avatar>
                    )}
                </div>
            ),
        },
        {
            title: 'Số quyết định',
            dataIndex: 'decisionCode',
            key: 'decisionCode',
            align: 'center',
            className: 'font-medium text-[#3e91ab] whitespace-nowrap cursor-pointer hover:underline',
            render: (text) => <Link to="/decision/detail" className="text-[#3e91ab]">{text}</Link>
        },
        {
            title: 'Ngày hiệu lực',
            dataIndex: 'effectiveDate',
            key: 'effectiveDate',
            align: 'center',
            className: 'whitespace-nowrap text-gray-600 dark:text-gray-400'
        },
        {
            title: 'Ngày quyết định',
            dataIndex: 'signDate',
            key: 'signDate',
            align: 'center',
            className: 'whitespace-nowrap text-gray-600 dark:text-gray-400'
        },

        {
            title: 'Loại quyết định',
            dataIndex: 'decisionType',
            key: 'decisionType',
            className: 'whitespace-nowrap',
            render: (text) => <span className="text-gray-700 dark:text-gray-300 font-medium">{text}</span>
        },

        {
            title: 'Tình trạng',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: 140,
            render: (status) => {
                let colorClass = '';
                let label = '';
                if (status === 'approved') {
                    colorClass = 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400';
                    label = 'Đã duyệt';
                }
                else if (status === 'pending') {
                    colorClass = 'bg-blue-50 text-blue-500 dark:bg-blue-900/20 dark:text-blue-400';
                    label = 'Đang duyệt';
                }
                else if (status === 'rejected') {
                    colorClass = 'bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400';
                    label = 'Không duyệt';
                }

                return <span className={`px-2.5 py-1 rounded text-[11px] font-medium inline-block whitespace-nowrap ${colorClass}`}>{label}</span>;
            }
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center',
            className: 'whitespace-nowrap text-gray-500 dark:text-gray-400 text-[13px]'
        }
    ];

    return (
        <Layout>
            <Helmet>
                <title> Danh sách quyết định</title>
            </Helmet>
            <div className="flex gap-2 mb-4 justify-between items-center">
                <h2 className="text-lg text-gray-900 font-semibold dark:text-white">
                    Danh sách quyết định
                </h2>
                {/* <button className="flex items-center gap-2 px-3 py-1.5 bg-[#3e91ab] text-white rounded text-sm font-medium hover:bg-[#357f96] transition-colors" onClick={() => navigate(`/employment-contract/add`)}>
                    <Plus size={16} /> Thêm mới
                </button> */}
            </div>

            <ConfigProvider theme={antdThemeConfig}>
                <div className={`contract-list-page min-h-screen transition-colors duration-300`}>

                    {/* Box chứa bảng */}
                    <div className={`w-full overflow-hidden flex flex-col rounded-md bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm`}>

                        {/* HEADER TABS TRẠNG THÁI */}
                        <div className={`flex justify-between items-end pt-3 border-b dark:border-gray-700 px-4`}>
                            <div className="overflow-x-auto no-scrollbar w-full">
                                <div className="flex gap-6 text-[13px] font-medium min-w-max pb-3">
                                    {[
                                        { key: 'all', label: 'Tất cả', count: counts.all },
                                        { key: 'pending', label: 'Chờ duyệt', count: counts.pending },
                                        { key: 'approved', label: 'Đã duyệt', count: counts.approved },
                                        { key: 'rejected', label: 'Không duyệt', count: counts.rejected },
                                    ].map(tab => (
                                        <div
                                            key={tab.key}
                                            onClick={() => setActiveTab(tab.key)}
                                            className={`cursor-pointer transition-colors pb-3 -mb-[13px] ${activeTab === tab.key
                                                ? 'text-[#3e91ab] border-b-2 border-[#3e91ab]'
                                                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                                                }`}
                                        >
                                            {tab.label} ({tab.count})
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* THANH TOOLBAR */}
                        <div className={`flex flex-col xl:flex-row items-center px-4 py-3 text-xs md:text-[13px] font-medium text-gray-600 dark:text-gray-300 border-b dark:border-[#374151] overflow-x-auto`}>
                            <div className="flex items-center gap-5 w-full xl:w-auto ml-auto min-w-max">
                                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><ListFilter size={14} className="text-gray-400" /> Lọc nhanh <ChevronDown size={14} className="text-gray-400" /></span>
                                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><FolderOutput size={14} className="text-gray-400" /> Export</span>
                                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><FolderInput size={14} className="text-gray-400" /> Import</span>
                                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><Settings size={14} className="text-gray-400" /> Cài đặt</span>
                            </div>
                        </div>

                        {/* BẢNG DỮ LIỆU */}
                        <div className="w-full">
                            <Table
                                columns={columns}
                                dataSource={filteredData}
                                rowSelection={{ type: 'checkbox' }}
                                pagination={false}
                                size="small"
                                className="custom-ant-table text-[13px]"
                                scroll={{ x: "max-content" }}
                            />
                        </div>

                    </div>
                </div>
            </ConfigProvider>
        </Layout>
    );
}